import React, { useMemo, useEffect } from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { useSearchBar } from './useSearchBar';
import { IconSearchSecond } from '../../components';
import LocationSelector from './LocationSelector';
import CalendarSelector from './CalendarSelector';
import CategorySelector from './CategorySelector';

import css from './MainFiltersForm.css';

const DesktopSearchBar = props => {
  const {
    formClassName,
    categoriesSuggestions = [],
    categories = [],
    latestSearches = [],
    pageName = 'SearchPage',
  } = props;

  const intl = useIntl();
  const logic = useSearchBar(pageName);

  const {
    formData,
    activeSection,
    setActiveSection,
    searchBarRef,
    recentWhat,
    recentWhere,
    handleDateSelect,
    handleLocationSelect,
    handleCategorySelect,
    handleWhatInputChange,
    handleLocationInputChange,
    handleClearWhat,
    handleClearWhere,
    handleClearDates,
    handleSubmit,
    handleClearRecentWhat,
    handleClearRecentWhere,
  } = logic;

  const whatPlaceholder = intl.formatMessage({ id: 'MarketplaceSearchBar.whatPlaceholder' });
  const wherePlaceholder = intl.formatMessage({ id: 'MarketplaceSearchBar.wherePlaceholder' });
  const whenPlaceholder = intl.formatMessage({ id: 'MarketplaceSearchBar.datesPlaceholder' });

  const dateDisplay = useMemo(() => {
    const { startDate, endDate } = formData;
    if (!startDate) return whenPlaceholder;
    const format = date => date.toLocaleDateString(intl.locale, { day: 'numeric', month: 'short' });
    if (startDate && !endDate) return format(startDate);
    if (startDate && endDate) return `${format(startDate)} - ${format(endDate)}`;
    return whenPlaceholder;
  }, [formData.startDate, formData.endDate, intl.locale, whenPlaceholder]);

  useEffect(() => {
    const handleClick = e => {
      if (searchBarRef.current && !searchBarRef.current.contains(e.target)) {
        setActiveSection(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [setActiveSection, searchBarRef]);

  return (
    <form
      className={classNames(formClassName, css.searchBarWrapper)}
      ref={searchBarRef}
      onSubmit={handleSubmit}
    >
      <div className={classNames(css.searchBar, { [css.hasActive]: !!activeSection })}>
        <div
          className={classNames(css.section, { [css.activeSection]: activeSection === 'what' })}
          onClick={() => setActiveSection('what')}
        >
          {activeSection === 'what' ? (
            <CategorySelector
              searchQuery={formData.what}
              suggestions={categoriesSuggestions}
              categories={categories}
              placeholder={whatPlaceholder}
              onInputChange={handleWhatInputChange}
              onClear={handleClearWhat}
              onSelect={handleCategorySelect}
              setActiveSection={setActiveSection}
              recentWhat={recentWhat}
              handleClearRecentWhat={handleClearRecentWhat}
            />
          ) : (
            <div className={css.inputWrapper}>
              <div
                className={classNames(css.valueDisplay, { [css.valueSelected]: !!formData.what })}
              >
                {formData.what || whatPlaceholder}
              </div>
              {formData.what && (
                <button type="button" className={css.clearBtn} onClick={handleClearWhat}>
                  <span className={css.clearIcon}>×</span>
                </button>
              )}
            </div>
          )}
        </div>

        <div className={css.divider} />

        <div
          className={classNames(css.section, { [css.activeSection]: activeSection === 'where' })}
          onClick={() => setActiveSection('where')}
        >
          {activeSection === 'where' ? (
            <LocationSelector
              searchQuery={formData.where}
              latestSearches={recentWhere}
              placeholder={wherePlaceholder}
              onInputChange={handleLocationInputChange}
              onClear={handleClearWhere}
              onSelect={handleLocationSelect}
              setActiveSection={setActiveSection}
              handleClearRecentWhere={handleClearRecentWhere}
            />
          ) : (
            <div className={css.inputWrapper}>
              <div
                className={classNames(css.valueDisplay, { [css.valueSelected]: !!formData.where })}
              >
                {formData.where || wherePlaceholder}
              </div>
              {formData.where && (
                <button type="button" className={css.clearBtn} onClick={handleClearWhere}>
                  <span className={css.clearIcon}>×</span>
                </button>
              )}
            </div>
          )}
        </div>

        <div className={css.divider} />
        <div
          className={classNames(css.section, { [css.activeSection]: activeSection === 'when' })}
          onClick={() => setActiveSection('when')}
        >
          <div className={css.inputWrapper}>
            <div
              className={classNames(css.valueDisplay, {
                [css.valueSelected]: !!formData.startDate,
              })}
            >
              {dateDisplay}
            </div>
            {formData.startDate && (
              <button type="button" className={css.clearBtn} onClick={handleClearDates}>
                <span className={css.clearIcon}>×</span>
              </button>
            )}
          </div>
          {activeSection === 'when' && (
            <CalendarSelector
              startDate={formData.startDate}
              endDate={formData.endDate}
              onSelect={handleDateSelect}
              intl={intl}
            />
          )}
        </div>

        <button className={css.searchBtn} type="submit">
          <IconSearchSecond className={css.searchIcon} />
        </button>
      </div>
    </form>
  );
};

export default DesktopSearchBar;
